/* collection-live-cart.js — Trade 15.4.1
   Fixes:
   - Sync quantités panier -> inputs (cartes collection + pop-up "Choisir les options")
   - Ne pas casser la saisie clavier dans la pop-up (bulk-add / quick order list)
   - Mettre à jour l'état des boutons +/- (classe disabled) pour éviter le curseur interdit
*/
(function () {
  const ACCEPT = { Accept: 'application/json' };

  const $ = (root, sel) => (root ? root.querySelector(sel) : null);
  const $$ = (root, sel) => (root ? Array.from(root.querySelectorAll(sel)) : []);

  // --- Cart cache
  const CART_CACHE = { ts: 0, data: null, pending: null };

  async function getCartFresh(maxAgeMs = 400) {
    const now = Date.now();
    if (CART_CACHE.data && now - CART_CACHE.ts < maxAgeMs) return CART_CACHE.data;
    if (CART_CACHE.pending) return CART_CACHE.pending;

    CART_CACHE.pending = fetch((window.routes && routes.cart_url) || '/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: ACCEPT,
    })
      .then((r) => r.json())
      .then((data) => {
        CART_CACHE.data = data;
        CART_CACHE.ts = Date.now();
        return data;
      })
      .finally(() => (CART_CACHE.pending = null));

    return CART_CACHE.pending;
  }

  function qtyForVariant(cart, variantId) {
    const vid = String(variantId);
    const it = (cart?.items || []).find((x) => String(x.variant_id) === vid);
    return Number(it?.quantity || 0);
  }

  function findVariantIdFromInput(input) {
    if (!input) return null;

    // 1) Trade: data-variant-id sur quantity-input
    const qi = input.closest('quantity-input');
    const vid1 = qi?.dataset?.variantId;
    if (vid1) return String(vid1);

    // 2) Trade: data-index sur l'input (souvent = variant id)
    const vid2 = input.dataset?.index;
    if (vid2) return String(vid2);

    // 3) quick-add-bulk data-index (collection)
    const qab = input.closest('quick-add-bulk');
    const vid3 = qab?.dataset?.index;
    if (vid3) return String(vid3);

    // 4) fallback
    const host = input.closest('[data-variant-id]');
    const vid4 = host?.getAttribute('data-variant-id');
    if (vid4) return String(vid4);

    return null;
  }

  function isInBulkPopupContext(input) {
    // Pop-up "Choisir les options" (quick-order-list / bulk-add)
    return !!input?.closest?.('bulk-add, .quick-order-list-container, quick-add-modal, modal-dialog, .quick-add-modal');
  }

  // Met à jour le state visuel des boutons +/- (évite le curseur interdit)
  function refreshQtyButtons(input) {
    if (!input) return;
    const qi = input.closest('quantity-input');
    if (qi && typeof qi.validateQtyRules === 'function') {
      try {
        qi.validateQtyRules();
        return;
      } catch (e) {}
    }

    // Fallback simple si validateQtyRules indispo
    const wrap = input.closest('.quantity, quantity-input') || input.parentElement;
    const minus = wrap?.querySelector?.(".quantity__button[name='minus']");
    const plus = wrap?.querySelector?.(".quantity__button[name='plus']");
    const v = parseInt(input.value || '0', 10) || 0;
    const min = input.min ? parseInt(input.min, 10) : 0;
    const max = input.max ? parseInt(input.max, 10) : null;

    if (minus) minus.classList.toggle('disabled', v <= min);
    if (plus && Number.isFinite(max)) plus.classList.toggle('disabled', v >= max);
  }

  function forceSetInput(input, qty) {
    if (!input) return;
    const q = Math.max(0, Number(qty || 0));

    input.value = String(q);
    input.setAttribute('value', String(q)); // Trade reset souvent depuis l'attribut
    input.dataset.cartQuantity = String(q);

    // IMPORTANT: ne PAS forcer min=0 ici.
    // Le thème utilise data-min / min pour ses règles. On respecte ce qui est déjà dans le DOM.

    refreshQtyButtons(input);
  }

  // Empêche le resync d'écraser une saisie clavier en cours
  function markUserEditing(input) {
    input.__liveEditingTs = Date.now();
  }
  function isUserEditingNow(input, windowMs = 700) {
    if (!input) return false;
    if (document.signal) {} // noop
    const ts = input.__liveEditingTs || 0;
    const active = document.activeElement === input;
    return active && Date.now() - ts < windowMs;
  }

  async function resyncInputFromCart(input) {
    if (!input) return;

    // Ne pas écraser pendant saisie clavier
    if (isUserEditingNow(input)) return;

    const vid = findVariantIdFromInput(input);
    if (!vid) return;

    const cart = await getCartFresh(0);
    const q = qtyForVariant(cart, vid);
    forceSetInput(input, q);
  }

  const QTY_INPUT_SELECTOR = `
    quick-add-bulk input.quantity__input,
    quick-add-bulk input[name="quantity"],

    quick-add-modal input.quantity__input,
    quick-add-modal input[name="quantity"],
    modal-dialog input.quantity__input,
    modal-dialog input[name="quantity"],
    .quick-add-modal input.quantity__input,
    .quick-add-modal input[name="quantity"],

    .quick-order-list-container input.quantity__input,
    .quick-order-list-container input[name="quantity"],
    bulk-add input.quantity__input,
    bulk-add input[name="quantity"]
  `;

  async function resyncAllQtyInputs(scope = document) {
    const cart = await getCartFresh(0);
    const inputs = $$(scope, QTY_INPUT_SELECTOR);

    inputs.forEach((input) => {
      // Ne pas écraser pendant saisie clavier
      if (isUserEditingNow(input)) return;

      const vid = findVariantIdFromInput(input);
      if (!vid) return;

      forceSetInput(input, qtyForVariant(cart, vid));
    });
  }

  // Bind events sans casser Trade :
  // - Sur cartes collection: input + change OK
  // - Dans la pop-up bulk-add: PAS de resync sur "input" (sinon ça remet 0), uniquement sur "change"
  function bindOneInput(input) {
    if (!input || input.__liveCartBound) return;

    const inPopup = isInBulkPopupContext(input);

    const scheduleResyncAfterTheme = () => {
      // Laisser Trade envoyer sa requête, puis relire le panier
      setTimeout(() => {
        CART_CACHE.ts = 0;
        resyncInputFromCart(input);
      }, 260);

      // un second passage au cas où (Trade peut batcher)
      setTimeout(() => {
        CART_CACHE.ts = 0;
        resyncInputFromCart(input);
      }, 600);
    };

    // 🔥 Si l'utilisateur tape au clavier: on marque l'édition pour éviter l'écrasement
    input.addEventListener('input', () => markUserEditing(input));

    // ✅ CHANGE = moment où Trade valide et déclenche bulk-add / queue
    input.addEventListener('change', () => {
      scheduleResyncAfterTheme();
    });

    // Sur les cartes, le + / - déclenche souvent change,
    // mais on garde un petit filet pour resync après clic bouton.
    const wrap = input.closest('.quantity, quantity-input') || input.parentElement;
    if (wrap) {
      wrap.addEventListener(
        'click',
        (e) => {
          const btn = e.target.closest?.('.quantity__button, [name="plus"], [name="minus"]');
          if (!btn) return;

          // Sur pop-up: on laisse Trade, mais on resync après.
          // Sur cartes: idem, ça corrige aussi l'état disabled du minus.
          scheduleResyncAfterTheme();
        },
        true
      );
    }

    // Sur blur: on resync (mais seulement si l'utilisateur n'est plus en train de taper)
    input.addEventListener('blur', () => {
      setTimeout(() => {
        CART_CACHE.ts = 0;
        resyncInputFromCart(input);
      }, 80);
    });

    // IMPORTANT :
    // On ne fait PAS de resync sur "input" (saisie clavier) => c'est exactement ce qui te remettait 0 en pop-up.
    // Sur cartes, l'ajout au panier est géré par Trade via click/change.

    input.__liveCartBound = true;
  }

  function bindAll(scope = document) {
    const inputs = $$(scope, QTY_INPUT_SELECTOR);
    inputs.forEach(bindOneInput);
  }

  // Quand on clique "Choisir les options", la modale se charge souvent async.
  document.addEventListener(
    'click',
    (e) => {
      const btn = e.target.closest?.('button, a');
      if (!btn) return;

      const txt = (btn.textContent || '').trim().toLowerCase();
      const looksLikeOptions =
        txt.includes('choisir les options') ||
        btn.matches?.('[data-modal], [aria-controls*="QuickAdd"], [data-quick-add-modal], [data-product-url]');

      if (!looksLikeOptions) return;

      CART_CACHE.ts = 0;
      setTimeout(() => {
        bindAll(document);
        resyncAllQtyInputs(document);
      }, 120);
      setTimeout(() => resyncAllQtyInputs(document), 380);
      setTimeout(() => resyncAllQtyInputs(document), 750);
    },
    true
  );

  async function boot() {
    bindAll(document);
    await resyncAllQtyInputs(document);

    setTimeout(() => resyncAllQtyInputs(document), 160);
    setTimeout(() => resyncAllQtyInputs(document), 420);
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);

  // Si le panier change ailleurs
  document.addEventListener('cart:refresh', () => {
    CART_CACHE.ts = 0;
    resyncAllQtyInputs(document);
  });
  window.addEventListener('cart:updated', () => {
    CART_CACHE.ts = 0;
    resyncAllQtyInputs(document);
  });

  // MutationObserver : cartes & modales ajoutées/remplacées
  const obs = new MutationObserver((muts) => {
    let touched = false;

    for (const m of muts) {
      for (const n of m.addedNodes || []) {
        if (!(n instanceof HTMLElement)) continue;

        if (
          n.matches?.('quick-add-bulk, quick-add-modal, modal-dialog, bulk-add, .quick-order-list-container, .card, .product-grid') ||
          n.querySelector?.(QTY_INPUT_SELECTOR)
        ) {
          bindAll(n);
          touched = true;
        }
      }
    }

    if (touched) {
      CART_CACHE.ts = 0;
      resyncAllQtyInputs(document);
      setTimeout(() => resyncAllQtyInputs(document), 180);
      setTimeout(() => resyncAllQtyInputs(document), 520);
    }
  });

  obs.observe(document.body, { childList: true, subtree: true });
})();
